import {
  createContext,
  Dispatch,
  MouseEvent,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from "react";
import Collapsible from "react-collapsible";
import { track } from "../../utils/tracking";
import { Icon } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import { Switch } from "../Inputs";
import { ButtonWrapper } from "./__styles__/Layer";
import { Group as GroupWrapper, GroupTitle } from "./__styles__/LayerSection";
import { Group, GroupId, LayerContext } from "./layers";
import { LayerSectionContext } from "./LayerSection";

export type LayerGroupContextType = {
  show: boolean;
  toggleShow: Dispatch<SetStateAction<boolean>>;
};

export const LayerGroupContext = createContext<
  LayerGroupContextType | undefined
>(undefined);

interface LayerGroupProps<G extends Group> {
  title: string;
  sectionName: string;
  testId: string;
  group: G;
  id?: GroupId<G>;
  children: ReactNode;
}

const LayerGroup = <G extends Group>({
  group,
  title,
  sectionName,
  testId,
  children,
  id,
}: LayerGroupProps<G>) => {
  const [show, toggleShow] = useState(false);
  const { toggleLayer, isLayerGroupVisible } = useContext(LayerContext);
  const { show: layerSectionExpanded } = useContext(LayerSectionContext) ?? {};
  const layerGroupId = `${group}-${title.replace(/ /g, "-").toLowerCase()}`;

  const handleToggle =
    ({
      group,
      id,
    }: {
      group: LayerGroupProps<G>["group"];
      id?: LayerGroupProps<G>["id"];
    }) =>
    (isVisible: boolean) => {
      if (group) {
        track("Map Layers Changed", {
          layerName: title,
          layerGroup: group,
          ...(id && { layerId: id }),
          visible: isVisible,
        });
        toggleLayer({
          group,
          id,
          isVisible,
        });
      }
    };

  const stopCollapsibleTrigger = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const switchName = title.toLowerCase().replace(/\s/g, "-");

  const trigger = (
    <GroupWrapper>
      <GroupTitle
        id={layerGroupId}
        role="button"
        aria-expanded={show}
        aria-controls={`${layerGroupId}-content`}
        tabIndex={layerSectionExpanded ? 0 : -1}
        data-testid={`${testId}-trigger`}
      >
        <Body size="small" type="regular">
          {title}
        </Body>
      </GroupTitle>
      <ButtonWrapper>
        <Icon
          iconName={show ? "chevron-up" : "chevron-down"}
          color={"contentSecondary"}
          size={12}
        />
        <span onClick={stopCollapsibleTrigger}>
          <Switch
            id={`${group}-${id}-switch`}
            name={switchName}
            onChange={handleToggle({ group, id })}
            value={group ? isLayerGroupVisible({ group, id }) : false}
            size="tiny"
            disabled={false}
            tabIndex={layerSectionExpanded ? 0 : -1}
          />
        </span>
      </ButtonWrapper>
    </GroupWrapper>
  );

  return (
    <LayerGroupContext.Provider value={{ show, toggleShow }}>
      <div data-testid={testId}>
        <div>
          <Collapsible
            trigger={trigger}
            onOpen={() => {
              toggleShow(true);
              track("Map Layers section opened", {
                sectionName,
                subSectionName: title,
              });
            }}
            onClose={() => toggleShow(false)}
            transitionTime={50}
          >
            <section
              id={`${layerGroupId}-content`}
              aria-labelledby={layerGroupId}
            >
              {children}
            </section>
          </Collapsible>
        </div>
      </div>
    </LayerGroupContext.Provider>
  );
};

export default LayerGroup;
